import React, { useState } from 'react';
import { moduleDetails, ModuleDetail } from '../data/moduleDetails';
import AvoModuleModal from './AvoModuleModal';

interface AvoModule {
  id: string;
  title: string;
  description: string;
  icon: string;
}

const avoModules: AvoModule[] = [
  {
    id: 'avo-procure',
    title: 'AVO Procure',
    description: 'Streamlined procurement planning, requisition management, and purchase order processing with automated workflows.',
    icon: '🛒',
  },
  {
    id: 'avo-ai',
    title: 'AVO AI',
    description: 'Intelligent assistant for automation, data insights, and AI-powered recommendations to optimize business processes.',
    icon: '🤖',
  },
  {
    id: 'avo-ebidding',
    title: 'AVO eBidding',
    description: 'Advanced reverse and sealed bidding platform with real-time scoring and transparent evaluation processes.',
    icon: '🏆',
  },
  {
    id: 'avo-vendor-portal',
    title: 'AVO Vendor Portal',
    description: 'Comprehensive supplier onboarding, document management, and communication platform for seamless vendor collaboration.',
    icon: '👥',
  },
  {
    id: 'avo-contract-lifecycle',
    title: 'AVO Contract Lifecycle',
    description: 'Digital contract templates, version control, and e-signature capabilities for end-to-end contract management.',
    icon: '📄',
  },
  {
    id: 'avo-analytics',
    title: 'AVO Analytics',
    description: 'Real-time dashboards, spend analysis, and performance KPIs with advanced reporting and visualization tools.',
    icon: '📊',
  },
  {
    id: 'avo-workflow',
    title: 'AVO Workflow',
    description: 'Visual builder for creating custom approval flows and business rules with drag-and-drop simplicity.',
    icon: '⚙️',
  },
  {
    id: 'avo-invoicing',
    title: 'AVO Invoicing',
    description: 'Automated invoice validation, status tracking, and seamless integration with financial systems.',
    icon: '💰',
  },
  {
    id: 'avo-risk-monitor',
    title: 'AVO Risk Monitor',
    description: 'Comprehensive vendor performance scoring and compliance alerts with risk assessment and monitoring.',
    icon: '🛡️',
  },
];

const AvoEcosystem: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState<ModuleDetail | null>(null);

  const handleLearnMore = (moduleId: string) => {
    const moduleDetail = moduleDetails.find(m => m.id === moduleId);
    if (moduleDetail) {
      setSelectedModule(moduleDetail);
      setModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedModule(null);
  };

  return (
    <section id="avo-ecosystem" className="py-20 bg-surface-soft">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-ink-900">
            AVO Ecosystem
          </h2>
          <p className="text-lg text-ink-500 max-w-3xl mx-auto leading-relaxed">
            A modular, scalable enterprise SaaS platform designed to streamline operations across your entire organization. 
            Each module integrates seamlessly to provide a comprehensive solution for modern business challenges.
          </p>
        </div>

        {/* Benefits Section */}
        <div className="mb-16">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end rounded-lg text-white text-xl">
                🔄
              </div>
              <h3 className="text-lg font-semibold mb-2 text-ink-900">Scalable Architecture</h3>
              <p className="text-ink-500">Grow with your business needs, adding modules as required</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end rounded-lg text-white text-xl">
                🔗
              </div>
              <h3 className="text-lg font-semibold mb-2 text-ink-900">Seamless Integration</h3>
              <p className="text-ink-500">Connect with existing systems and third-party applications</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end rounded-lg text-white text-xl">
                ⚡
              </div>
              <h3 className="text-lg font-semibold mb-2 text-ink-900">Automation First</h3>
              <p className="text-ink-500">Reduce manual tasks and increase operational efficiency</p>
            </div>
          </div>
        </div>
        
        {/* Modules Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {avoModules.map((module) => (
            <div key={module.id} className="glass-card p-8 text-center hover:transform hover:scale-105 transition-transform duration-300">
              <div className="w-16 h-16 mx-auto mb-6 flex items-center justify-center bg-gradient-to-r from-brand-start to-brand-end rounded-lg text-white text-2xl">
                {module.icon}
              </div>
              <h3 className="text-xl font-semibold mb-4 text-ink-900">
                {module.title}
              </h3>
              <p className="text-ink-500 leading-relaxed mb-6">
                {module.description}
              </p>
              <button
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-brand-start to-brand-end text-white rounded-lg hover:from-brand-end hover:to-brand-start transition-all duration-300 text-sm font-medium"
                onClick={() => handleLearnMore(module.id)}
              >
                Learn More
                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          ))}
        </div>

        {/* Enhanced Modal */}
        <AvoModuleModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          module={selectedModule}
        />

        {/* CTA Section */}
        <div className="text-center mt-16">
          <div className="glass-card p-8 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4 text-ink-900">
              Ready to Transform Your Operations?
            </h3>
            <p className="text-ink-500 mb-6">
              Discover how the AVO Ecosystem can streamline your business processes and drive efficiency across your organization.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                className="px-8 py-3 bg-gradient-to-r from-brand-start to-brand-end text-white rounded-lg hover:from-brand-end hover:to-brand-start transition-all duration-300 font-medium"
                onClick={() => {
                  const contactSection = document.querySelector('#contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Request Demo
              </button>
              <button
                className="px-8 py-3 border-2 border-brand-start text-brand-start rounded-lg hover:bg-brand-start hover:text-white transition-all duration-300 font-medium"
                onClick={() => {
                  const contactSection = document.querySelector('#contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Contact Sales
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AvoEcosystem; 